import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Search, X, ChevronDown, User } from 'lucide-react';
import type { Customer } from './CustomerManagement';
import { api } from '../lib/api';

interface CustomerSelectorProps {
  darkMode: boolean;
  onCustomerSelect: (customer: Customer | null) => void;
  initialCustomer?: Customer | null;
}

export function CustomerSelector({ darkMode, onCustomerSelect, initialCustomer }: CustomerSelectorProps) {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });

  const triggerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const scrollYRef = useRef(0);

  // Load customers from API
  useEffect(() => {
    api.get<Customer[]>('/api/customers').then(setCustomers).catch(() => {});
  }, []);

  // Set initial customer if provided
  useEffect(() => {
    if (initialCustomer) setSelectedCustomer(initialCustomer);
  }, [initialCustomer]);

  // Calculate dropdown position from trigger rect
  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setDropdownPos({
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
    });
  }, []);

  // Body scroll lock
  useEffect(() => {
    if (isOpen) {
      scrollYRef.current = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollYRef.current}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
    } else {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      window.scrollTo(0, scrollYRef.current);
    }
    return () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
    };
  }, [isOpen]);

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return;
    const handleMouseDown = (e: MouseEvent) => {
      if (
        triggerRef.current?.contains(e.target as Node) ||
        dropdownRef.current?.contains(e.target as Node)
      ) {
        return;
      }
      setIsOpen(false);
      setSearch('');
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen]);

  // Focus input when opening
  useEffect(() => {
    if (isOpen) {
      updatePosition();
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [isOpen, updatePosition]);

  const filtered = customers.filter((c) => {
    const q = search.toLowerCase();
    return (c.name || '').toLowerCase().includes(q) || (c.email || '').toLowerCase().includes(q);
  });

  const handleSelect = (customer: Customer) => {
    setSelectedCustomer(customer);
    onCustomerSelect(customer);
    setIsOpen(false);
    setSearch('');
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedCustomer(null);
    onCustomerSelect(null);
    setSearch('');
  };

  const openDropdown = () => {
    setIsOpen(true);
    setSearch('');
  };

  const dropdown = isOpen
    ? createPortal(
        <div
          ref={dropdownRef}
          style={{
            position: 'fixed',
            top: dropdownPos.top,
            left: dropdownPos.left,
            width: dropdownPos.width,
            zIndex: 9999,
          }}
          className={`rounded-lg border shadow-lg ${
            darkMode ? 'bg-slate-700 border-slate-600' : 'bg-white border-slate-200'
          }`}
        >
          <div
            className="max-h-64 overflow-y-auto"
            style={{ overscrollBehavior: 'contain' }}
            onWheel={(e) => e.stopPropagation()}
          >
            {selectedCustomer && !search && (
              <button
                onClick={() => {
                  setSelectedCustomer(null);
                  onCustomerSelect(null);
                  setIsOpen(false);
                  setSearch('');
                }}
                className={`w-full px-4 py-3 text-left text-sm border-b transition-colors ${
                  darkMode
                    ? 'hover:bg-slate-600 border-slate-600 text-slate-400 italic'
                    : 'hover:bg-slate-50 border-slate-200 text-slate-500 italic'
                }`}
              >
                Auswahl aufheben
              </button>
            )}
            {filtered.length > 0 ? (
              filtered.map((customer) => (
                <button
                  key={customer.id}
                  onClick={() => handleSelect(customer)}
                  className={`w-full px-4 py-3 text-left transition-colors ${
                    selectedCustomer?.id === customer.id
                      ? darkMode
                        ? 'bg-blue-900/30 text-blue-300'
                        : 'bg-blue-50 text-blue-700'
                      : darkMode
                        ? 'hover:bg-slate-600 text-white'
                        : 'hover:bg-slate-50 text-slate-900'
                  }`}
                >
                  <div className="font-medium">{customer.name}</div>
                  <div
                    className={`text-xs mt-1 ${
                      darkMode ? 'text-slate-400' : 'text-slate-600'
                    }`}
                  >
                    {customer.email}
                  </div>
                </button>
              ))
            ) : (
              <div
                className={`px-4 py-8 text-center text-sm ${
                  darkMode ? 'text-slate-400' : 'text-slate-500'
                }`}
              >
                {customers.length === 0 ? (
                  <>
                    <User className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>Noch keine Kunden angelegt</p>
                    <p className="text-xs mt-1">Legen Sie im Dashboard Kunden an</p>
                  </>
                ) : (
                  <p>Keine Treffer für &ldquo;{search}&rdquo;</p>
                )}
              </div>
            )}
          </div>
        </div>,
        document.body,
      )
    : null;

  return (
    <div className="relative">
      <label
        className={`block text-sm font-medium mb-2 ${
          darkMode ? 'text-slate-300' : 'text-slate-700'
        }`}
      >
        Kunde auswählen
      </label>

      <div
        ref={triggerRef}
        onClick={() => {
          if (!isOpen) openDropdown();
        }}
        className={`w-full flex items-center gap-2 px-4 py-2.5 border rounded-lg cursor-pointer focus-within:ring-2 focus-within:ring-blue-500 ${
          darkMode
            ? 'bg-slate-700 border-slate-600 text-white'
            : 'bg-white border-slate-300 text-slate-900'
        }`}
      >
        {isOpen ? (
          <>
            <Search className="w-4 h-4 opacity-50 shrink-0" />
            <input
              ref={inputRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Kunde suchen..."
              className={`flex-1 bg-transparent outline-none text-sm ${
                darkMode
                  ? 'text-white placeholder:text-slate-400'
                  : 'text-slate-900 placeholder:text-slate-400'
              }`}
            />
          </>
        ) : selectedCustomer ? (
          <>
            <span className="flex-1 truncate text-sm">{selectedCustomer.name}</span>
            <button
              type="button"
              onClick={handleClear}
              className={`shrink-0 p-0.5 rounded hover:bg-black/10 ${
                darkMode ? 'hover:bg-white/10' : ''
              }`}
            >
              <X className="w-4 h-4" />
            </button>
            <ChevronDown className="w-4 h-4 shrink-0 opacity-50" />
          </>
        ) : (
          <>
            <Search className="w-4 h-4 opacity-50 shrink-0" />
            <span className="flex-1 text-sm opacity-50">Kunde suchen...</span>
            <ChevronDown className="w-4 h-4 shrink-0 opacity-50" />
          </>
        )}
      </div>

      {dropdown}
    </div>
  );
}
